import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Logo } from "../components/Logo";

export default function ConnectorSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("checking"); // checking | ready | error
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Setup link is missing. Please use the link in your invitation email.");
      return;
    }
    api.get(`/connector/setup?token=${encodeURIComponent(token)}`)
      .then((r) => { setEmail(r.data.email); setName(r.data.name || ""); setStatus("ready"); })
      .catch((e) => { setStatus("error"); setError(formatApiErrorDetail(e.response?.data?.detail) || "This setup link is not valid or has expired."); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/connector/setup", { token, password });
      setUser(data);
      navigate("/connector", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not complete setup. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6 py-10">
      <div data-testid="connector-setup-card" className="w-full max-w-md border border-line bg-panel p-8">
        <div className="flex items-center gap-3 mb-6"><Logo imgClass="h-7" /><span className="font-mono-data text-[11px] uppercase tracking-wider text-ftext3">Connector setup</span></div>
        {status === "checking" && <div className="text-ftext3 font-mono-data text-sm">Verifying your setup link…</div>}
        {status === "error" && <div data-testid="connector-setup-error" className="text-[#FCA5A5] font-mono-data text-sm">{error}</div>}
        {status === "ready" && (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-ftext2 text-sm">Welcome <span className="text-ftext font-semibold">{name || email}</span>. Choose a password to activate your Connector account for <span className="font-mono-data text-xs text-ftext3">{email}</span>.</div>
            <div className="space-y-1.5">
              <label className="font-mono-data text-xs uppercase tracking-wider text-ftext2">New password</label>
              <Input data-testid="connector-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-none bg-ink border-line2" autoFocus required minLength={8} />
            </div>
            <div className="space-y-1.5">
              <label className="font-mono-data text-xs uppercase tracking-wider text-ftext2">Confirm password</label>
              <Input data-testid="connector-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none bg-ink border-line2" required minLength={8} />
            </div>
            {error && <div role="alert" className="text-[#FCA5A5] font-mono-data text-xs">{error}</div>}
            <button data-testid="connector-setup-submit" type="submit" disabled={submitting} className="w-full bg-sky hover:bg-skyH text-primary-foreground font-mono-data text-sm font-semibold uppercase tracking-wider px-4 py-2.5 disabled:opacity-60 transition-colors">
              {submitting ? "Activating…" : "Activate account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
